// Convert arrays to Sets for existence checks (keep as is - already optimized)
import { AvailabilityColumns, LeaseColumns, PropertyColumns } from '@miq/catalog';
import { DataObject } from '../../../models/report.model';
import { DisplayFnArgumentsInfo, LovValues } from '../../../models/template.model';

const measurementTypeColumnsSet = new Set([
  AvailabilityColumns.AvailabilityMeasurements_Type,
  PropertyColumns.PropertyMeasurements_Type,
  LeaseColumns.LeaseMeasurements_Type,
]);

const measurementValueColumnsSet = new Set([
  AvailabilityColumns.AvailabilityMeasurements_Value,
  PropertyColumns.PropertyMeasurements_Value,
  LeaseColumns.LeaseMeasurements_Value,
]);

const measurementUnitColumnsSet = new Set([
  AvailabilityColumns.AvailabilityMeasurements_ElementNumber,
  PropertyColumns.PropertyMeasurements_ElementNumber,
  LeaseColumns.LeaseMeasurements_Unit,
]);

// Consolidated mapping object for all measurement types
const MEASUREMENT_COLUMN_MAPPING = {
  type: {
    [AvailabilityColumns.AvailabilityMeasurements_Type]: 'property_measurement_availability',
    [PropertyColumns.PropertyMeasurements_Type]: 'property_measurement',
    [LeaseColumns.LeaseMeasurements_Type]: 'property_measurement_arrangement',
  },
  value: {
    [AvailabilityColumns.AvailabilityMeasurements_Value]: 'property_measurement_availability',
    [PropertyColumns.PropertyMeasurements_Value]: 'property_measurement',
    [LeaseColumns.LeaseMeasurements_Value]: 'property_measurement_arrangement',
  },
  unit: {
    [AvailabilityColumns.AvailabilityMeasurements_ElementNumber]: 'property_measurement_availability',
    [PropertyColumns.PropertyMeasurements_ElementNumber]: 'property_measurement',
    [LeaseColumns.LeaseMeasurements_Unit]: 'property_measurement_arrangement',
  },
} as const;

/**
 * Optimized column type checking functions
 */
export const isMeasurementTypeColumn = (columnId: number): boolean => measurementTypeColumnsSet.has(columnId);
export const isMeasurementValueColumn = (columnId: number): boolean => measurementValueColumnsSet.has(columnId);
export const isMeasurementUnitColumn = (columnId: number): boolean => measurementUnitColumnsSet.has(columnId);

/**
 * Optimized measurement filtering with early returns and single iteration
 */
const getFilteredMeasurements = (measurements: any[]): any[] => {
  if (!Array.isArray(measurements) || measurements.length === 0) {
    return [];
  }

  return measurements.filter(
    measurement => measurement && measurement !== '' && measurement.property_element_type_desc !== 'Total Area'
  );
};

/**
 * Generic measurement formatter base function
 */
const formatMeasurements = (
  recordData: DataObject,
  column: DisplayFnArgumentsInfo,
  mappingType: keyof typeof MEASUREMENT_COLUMN_MAPPING,
  formatter: (measurement: any, lookupMap?: LovValues[]) => string,
  lookupMap?: LovValues[]
): string => {
  // Early validation
  if (!column?.columnId || !recordData?.data) {
    return '';
  }

  // Get property path from consolidated mapping
  const propertyPath = MEASUREMENT_COLUMN_MAPPING[mappingType][column.columnId];
  if (!propertyPath) {
    return '';
  }

  const measurements = recordData.data[propertyPath];
  if (!measurements || !Array.isArray(measurements)) {
    return '';
  }

  // Filter and format in single pass
  const filteredMeasurements = getFilteredMeasurements(measurements);
  if (filteredMeasurements.length === 0) {
    return '';
  }

  // Map with provided formatter
  const formattedValues = filteredMeasurements.map(measurement => formatter(measurement, lookupMap));

  return formattedValues.join(' | ');
};

/**
 * Optimized measurement description formatter with memoization opportunity
 */
const formatMeasurementDescription = (measurement: DataObject, lookupMap?: LovValues[]): string => {
  const { property_element_type_desc, element_number } = measurement;

  if (!property_element_type_desc) {
    return '';
  }

  // Transform using lookup map (optimized with early return)
  let transformedDesc = property_element_type_desc;

  if (lookupMap?.length) {
    const foundItem = lookupMap.find(item => item.key === property_element_type_desc);
    if (foundItem?.value) {
      transformedDesc = foundItem.value;
    }
  }

  return element_number ? `${transformedDesc} ${element_number}` : transformedDesc;
};

/**
 * Optimized measurement value formatter
 */
const formatMeasurementValue = (measurement: any): string => {
  const { property_measurement_size } = measurement;

  return property_measurement_size ? Number(property_measurement_size).toLocaleString('en-US') : '-';
};

/**
 * Optimized measurement unit formatter
 */
const formatMeasurementUnit = (measurement: any): string => {
  const { element_number } = measurement;

  return element_number ? Number(element_number).toLocaleString('en-US') : '-';
};

/**
 * Optimized property element type formatter
 */
export const propertyElementTypeFormatter = (
  recordData: DataObject,
  column: DisplayFnArgumentsInfo,
  lovData?: LovValues[]
): string => {
  if (!isMeasurementTypeColumn(column.columnId)) {
    return '';
  }

  return formatMeasurements(recordData, column, 'type', formatMeasurementDescription, lovData);
};

/**
 * Optimized property measurement value formatter
 */
export const propertyMeasurementValueFormatter = (recordData: DataObject, column: DisplayFnArgumentsInfo): string => {
  if (!isMeasurementValueColumn(column.columnId)) {
    return '';
  }

  return formatMeasurements(recordData, column, 'value', formatMeasurementValue);
};

/**
 * Optimized property measurement element number formatter
 */
export const propertyMeasurementElementNumberFormatter = (
  recordData: DataObject,
  column: DisplayFnArgumentsInfo
): string => {
  if (!isMeasurementUnitColumn(column.columnId)) {
    return '';
  }

  return formatMeasurements(recordData, column, 'unit', formatMeasurementUnit);
};

/**
 * Utility function for bulk measurement processing (if needed)
 */
export const formatAllMeasurements = (
  recordData: DataObject,
  columns: DisplayFnArgumentsInfo[],
  lovData?: LovValues[]
): Record<string, string> => {
  const results: Record<string, string> = {};

  columns.forEach(column => {
    const key = `column_${column.columnId}`;

    if (isMeasurementTypeColumn(column.columnId)) {
      results[key] = propertyElementTypeFormatter(recordData, column, lovData);
    } else if (isMeasurementValueColumn(column.columnId)) {
      results[key] = propertyMeasurementValueFormatter(recordData, column);
    } else if (isMeasurementUnitColumn(column.columnId)) {
      results[key] = propertyMeasurementElementNumberFormatter(recordData, column);
    }
  });

  return results;
};
